import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Table, Input, Select, DatePicker, Skeleton, Empty } from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  CalendarOutlined,
  CalendarTwoTone,
  ClearOutlined,
} from "@ant-design/icons";
import Pagination from "@mui/material/Pagination";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { fetchDemoBookings } from "../services/demoBookingService";
import { useInternalUserList } from "../hooks/useManageEnquiry";
import { isSuperAdmin, currentUserId } from "../utils/role";

const PAGE_SIZE = 10;

const STATUS_STYLES = {
  pending:   { background: "#fffbeb", color: "#d97706", border: "1px solid #fde68a" },
  confirmed: { background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0" },
  done:      { background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe" },
  cancelled: { background: "#f3f4f6", color: "#6b7280", border: "1px solid #d1d5db" },
};

const MODE_STYLES = {
  video: { background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe", icon: "🎥", label: "Video" },
  phone: { background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0", icon: "📞", label: "Phone" },
};

const badgeStyle = (styles) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  padding: "3px 10px",
  borderRadius: "20px",
  fontSize: "12px",
  fontWeight: 600,
  whiteSpace: "nowrap",
  ...styles,
});

const EMPTY_FILTER = {
  search: "",
  demo_mode: "",
  demo_status: "",
  start_date: null,
  end_date: null,
  // Super-admin only — normal admins are always scoped to themselves
  assign_to: "",
  is_assigned: "",
};

const DemoBookingList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState({ ...EMPTY_FILTER });
  const [appliedFilter, setAppliedFilter] = useState({ ...EMPTY_FILTER });
  const [filtersOpen, setFiltersOpen] = useState(true);
  const debounceRef = useRef(null);

  const superAdmin = isSuperAdmin();
  const myUserId   = currentUserId();

  // Internal users for the super-admin "Assigned To" dropdown
  const { data: internalUsers = [] } = useInternalUserList();

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setAppliedFilter((prev) => ({ ...prev, search: filter.search }));
      setPage(1);
    }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [filter.search]);

  const buildParams = () => {
    const p = { page, limit: PAGE_SIZE };
    if (appliedFilter.search)      p.search      = appliedFilter.search;
    if (appliedFilter.demo_mode)   p.demo_mode   = appliedFilter.demo_mode;
    if (appliedFilter.demo_status) p.demo_status = appliedFilter.demo_status;
    if (appliedFilter.start_date)  p.start_date  = appliedFilter.start_date;
    if (appliedFilter.end_date)    p.end_date    = appliedFilter.end_date;

    if (superAdmin) {
      // Super-admin sees everything by default; the two dropdowns narrow it down.
      if (appliedFilter.assign_to)  p.assign_to  = appliedFilter.assign_to;
      if (appliedFilter.is_assigned !== "" && appliedFilter.is_assigned != null) {
        p.is_assigned = appliedFilter.is_assigned;
      }
    } else {
      // Everyone else is hard-scoped to their own demo bookings — not a
      // clearable UI filter, so it is applied on every request.
      p.assign_to = myUserId;
      p.is_assigned = 1;
    }
    return p;
  };

  const { data, isFetching, isError } = useQuery({
    queryKey: ["demo-bookings", page, appliedFilter],
    queryFn: () => fetchDemoBookings(buildParams()),
    keepPreviousData: true,
    onError: () => toast.error("Failed to load demo bookings"),
  });

  useEffect(() => {
    if (isError) toast.error("Failed to load demo bookings");
  }, [isError]);

  const bookings = data?.data || [];
  // total_records is the FILTERED count, so page count must come from it
  const total      = data?.pagination?.total_records ?? data?.total_records ?? 0;
  const totalPages = data?.pagination?.total_pages ?? Math.ceil(total / PAGE_SIZE) ?? 1;

  const activeFilterCount = [
    appliedFilter.demo_mode,
    appliedFilter.demo_status,
    appliedFilter.start_date,
    appliedFilter.end_date,
    // is_assigned can legitimately be 0 (Unassigned), so test for "set", not truthy
    superAdmin ? appliedFilter.assign_to : "",
    superAdmin && appliedFilter.is_assigned !== "" ? "is_assigned" : "",
  ].filter(Boolean).length;

  const handleApply = () => {
    setAppliedFilter({ ...filter });
    setPage(1);
  };

  const handleClear = () => {
    setFilter({ ...EMPTY_FILTER });
    setAppliedFilter({ ...EMPTY_FILTER });
    setPage(1);
  };

  const thStyle = {
    padding: "14px 16px",
    textAlign: "left",
    fontSize: "12px",
    fontWeight: 700,
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    whiteSpace: "nowrap",
    background: "linear-gradient(135deg,#f8f9ff 0%,#f1f5f9 100%)",
  };

  const columns = [
    {
      title: <span style={{ ...thStyle, padding: 0 }}>#</span>,
      key: "index",
      width: 50,
      align: "center",
      render: (_, __, idx) => (
        <span style={{ fontSize: "13px", color: "#9ca3af", fontWeight: 600 }}>
          {(page - 1) * PAGE_SIZE + idx + 1}
        </span>
      ),
    },
    {
      title: <span style={{ ...thStyle, padding: 0 }}>Lead Name</span>,
      key: "name",
      render: (row) => (
        <span
          onClick={() => navigate(`/leads/demo-booking/${row.lead_id}`)}
          style={{ fontWeight: 700, color: "#2563eb", cursor: "pointer", fontSize: "14px" }}
        >
          {row.name || "—"}
        </span>
      ),
    },
    {
      title: <span style={{ ...thStyle, padding: 0 }}>Company</span>,
      dataIndex: "company",
      key: "company",
      render: (v) => <span style={{ fontSize: "13px", color: "#374151" }}>{v || "—"}</span>,
    },
    {
      title: <span style={{ ...thStyle, padding: 0 }}>Email</span>,
      dataIndex: "email",
      key: "email",
      render: (v) => <span style={{ fontSize: "13px", color: "#374151" }}>{v || "—"}</span>,
    },
    {
      title: <span style={{ ...thStyle, padding: 0 }}>Phone</span>,
      dataIndex: "phone",
      key: "phone",
      render: (v) => <span style={{ fontSize: "13px", color: "#374151" }}>{v || "—"}</span>,
    },
    {
      title: <span style={{ ...thStyle, padding: 0 }}>Demo Date</span>,
      key: "demo_date",
      render: (row) => (
        <span style={{ fontSize: "13px", color: "#374151", whiteSpace: "nowrap" }}>
          {row.demo_date_formatted || "—"}
        </span>
      ),
    },
    {
      title: <span style={{ ...thStyle, padding: 0 }}>Mode</span>,
      dataIndex: "demo_mode",
      key: "demo_mode",
      render: (v) => {
        const s = MODE_STYLES[v];
        if (!s) return <span style={{ color: "#9ca3af", fontSize: "12px" }}>—</span>;
        return <span style={badgeStyle(s)}>{s.icon} {s.label}</span>;
      },
    },
    {
      title: <span style={{ ...thStyle, padding: 0 }}>Status</span>,
      dataIndex: "demo_status",
      key: "demo_status",
      render: (v) => {
        const s = STATUS_STYLES[v];
        if (!s) return <span style={{ color: "#9ca3af", fontSize: "12px" }}>—</span>;
        return (
          <span style={badgeStyle(s)}>
            {v.charAt(0).toUpperCase() + v.slice(1)}
          </span>
        );
      },
    },
    {
      title: <span style={{ ...thStyle, padding: 0 }}>Assigned To</span>,
      dataIndex: "assign_to_name",
      key: "assign_to_name",
      render: (v) =>
        v ? (
          <span style={{ fontSize: "13px", color: "#374151" }}>{v}</span>
        ) : (
          <span style={{ fontSize: "12px", color: "#9ca3af", fontStyle: "italic" }}>
            Not assigned
          </span>
        ),
    },
    {
      title: <span style={{ ...thStyle, padding: 0 }}>Next Follow-up</span>,
      dataIndex: "next_followup_date",
      key: "next_followup_date",
      render: (v) => (
        <span style={{ fontSize: "13px", color: v ? "#374151" : "#d1d5db", whiteSpace: "nowrap" }}>
          {v || "—"}
        </span>
      ),
    },
  ];

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* Header */}
          <div style={{ background: "linear-gradient(135deg,#5b48e8 0%,#7c3aed 100%)", borderRadius: "16px", padding: "28px 32px", marginBottom: "24px", boxShadow: "0 8px 32px rgba(91,72,232,0.28)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <div style={{ width: "56px", height: "56px", background: "rgba(255,255,255,0.18)", borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "26px" }}>
                📅
              </div>
              <div>
                <h4 style={{ color: "white", margin: 0, fontSize: "24px", fontWeight: 700 }}>Demo Bookings</h4>
                <p style={{ color: "rgba(255,255,255,0.85)", margin: 0, fontSize: "14px" }}>
                  Track and manage scheduled demo sessions
                </p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              {total > 0 && (
                <span style={{ background: "rgba(255,255,255,0.2)", color: "white", border: "1px solid rgba(255,255,255,0.3)", borderRadius: "20px", padding: "5px 16px", fontSize: "14px", fontWeight: 700 }}>
                  {total} total
                </span>
              )}
            </div>
          </div>

          {/* Filter Card */}
          <div style={{ background: "white", borderRadius: "14px", border: "1px solid #e5e7eb", boxShadow: "0 2px 12px rgba(0,0,0,0.05)", marginBottom: "20px", overflow: "hidden" }}>
            <div
              onClick={() => setFiltersOpen((o) => !o)}
              style={{ padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", background: "#fafbff", borderBottom: filtersOpen ? "1px solid #e5e7eb" : "none" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <FilterOutlined style={{ color: "#5b48e8", fontSize: "16px" }} />
                <span style={{ fontWeight: 700, fontSize: "15px", color: "#1f2937" }}>Filters</span>
                {activeFilterCount > 0 && (
                  <span style={{ background: "#5b48e8", color: "white", borderRadius: "20px", padding: "1px 10px", fontSize: "12px", fontWeight: 700 }}>
                    {activeFilterCount} active
                  </span>
                )}
              </div>
              <span style={{ color: "#6b7280", fontSize: "13px" }}>{filtersOpen ? "▲ Collapse" : "▼ Expand"}</span>
            </div>

            {filtersOpen && (
              <div style={{ padding: "20px 24px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "14px", marginBottom: "16px" }}>
                  <Input
                    prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                    placeholder="Search name, email, phone..."
                    value={filter.search}
                    onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))}
                    style={{ borderRadius: "9px", height: "40px" }}
                    allowClear
                  />

                  <Select
                    value={filter.demo_mode || undefined}
                    onChange={(v) => setFilter((f) => ({ ...f, demo_mode: v || "" }))}
                    placeholder="All Modes"
                    allowClear
                    style={{ width: "100%", height: "40px" }}
                  >
                    <Select.Option value="video">🎥 Video</Select.Option>
                    <Select.Option value="phone">📞 Phone</Select.Option>
                  </Select>

                  <Select
                    value={filter.demo_status || undefined}
                    onChange={(v) => setFilter((f) => ({ ...f, demo_status: v || "" }))}
                    placeholder="All Statuses"
                    allowClear
                    style={{ width: "100%", height: "40px" }}
                  >
                    <Select.Option value="pending">Pending</Select.Option>
                    <Select.Option value="confirmed">Confirmed</Select.Option>
                    <Select.Option value="done">Done</Select.Option>
                    <Select.Option value="cancelled">Cancelled</Select.Option>
                  </Select>

                  {/* Assignment filters — super-admin only. Normal admins are
                      always scoped to their own bookings, so no control here. */}
                  {superAdmin && (
                    <Select
                      value={filter.assign_to || undefined}
                      onChange={(v) => setFilter((f) => ({ ...f, assign_to: v || "" }))}
                      placeholder="Assigned To: All"
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      style={{ width: "100%", height: "40px" }}
                    >
                      {internalUsers.map((u) => (
                        <Select.Option key={u.id} value={u.id}>
                          {u.name || u.email || `User #${u.id}`}
                        </Select.Option>
                      ))}
                    </Select>
                  )}

                  {superAdmin && (
                    <Select
                      value={filter.is_assigned === "" ? undefined : filter.is_assigned}
                      onChange={(v) => setFilter((f) => ({ ...f, is_assigned: v === undefined ? "" : v }))}
                      placeholder="Assignment: All"
                      allowClear
                      style={{ width: "100%", height: "40px" }}
                    >
                      <Select.Option value={1}>Assigned</Select.Option>
                      <Select.Option value={0}>Unassigned</Select.Option>
                    </Select>
                  )}

                  <DatePicker
                    placeholder="Start Date"
                    format="DD/MM/YYYY"
                    value={filter.start_date ? dayjs(filter.start_date) : null}
                    onChange={(_, dateStr) => setFilter((f) => ({ ...f, start_date: dateStr ? dayjs(dateStr, "DD/MM/YYYY").format("YYYY-MM-DD") : null }))}
                    style={{ width: "100%", height: "40px", borderRadius: "9px" }}
                    suffixIcon={<CalendarOutlined style={{ color: "#9ca3af" }} />}
                  />

                  <DatePicker
                    placeholder="End Date"
                    format="DD/MM/YYYY"
                    value={filter.end_date ? dayjs(filter.end_date) : null}
                    onChange={(_, dateStr) => setFilter((f) => ({ ...f, end_date: dateStr ? dayjs(dateStr, "DD/MM/YYYY").format("YYYY-MM-DD") : null }))}
                    style={{ width: "100%", height: "40px", borderRadius: "9px" }}
                    suffixIcon={<CalendarOutlined style={{ color: "#9ca3af" }} />}
                  />
                </div>

                <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                  <button
                    onClick={handleClear}
                    style={{ height: "38px", padding: "0 20px", borderRadius: "9px", border: "1px solid #e5e7eb", background: "white", color: "#6b7280", fontWeight: 600, fontSize: "13px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}
                  >
                    <ClearOutlined /> Clear
                  </button>
                  <button
                    onClick={handleApply}
                    style={{ height: "38px", padding: "0 24px", borderRadius: "9px", border: "none", background: "linear-gradient(135deg,#5b48e8,#7c3aed)", color: "white", fontWeight: 700, fontSize: "13px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", boxShadow: "0 2px 8px rgba(91,72,232,0.3)" }}
                  >
                    <FilterOutlined /> Apply Filters
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Table Card */}
          <div style={{ background: "white", borderRadius: "16px", boxShadow: "0 2px 16px rgba(0,0,0,0.07)", border: "1px solid #f0f0f0", overflow: "hidden" }}>
            <div style={{ padding: "18px 24px", borderBottom: "2px solid #f5f5f5", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ width: "38px", height: "38px", background: "linear-gradient(135deg,#5b48e8,#7c3aed)", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "17px" }}>📅</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "16px", color: "#1f2937" }}>All Demo Bookings</div>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>{total} booking{total !== 1 ? "s" : ""} found</div>
                </div>
              </div>
            </div>

            {isFetching ? (
              <div style={{ padding: "24px" }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} active paragraph={{ rows: 1 }} style={{ marginBottom: "14px" }} />
                ))}
              </div>
            ) : bookings.length === 0 ? (
              <div style={{ padding: "60px 24px", textAlign: "center" }}>
                <Empty
                  description={
                    <span style={{ color: "#9ca3af" }}>
                      {/* A normal admin only sees what the auto-assign cron has
                          given them, so say that rather than "none found". */}
                      {!superAdmin && activeFilterCount === 0 && !appliedFilter.search
                        ? "No demo bookings assigned yet"
                        : "No demo bookings found"}
                    </span>
                  }
                />
                {!superAdmin && activeFilterCount === 0 && !appliedFilter.search && (
                  <div style={{ fontSize: 12, color: "#c4b5fd", marginTop: 6 }}>
                    Bookings appear here once they are assigned to you.
                  </div>
                )}
              </div>
            ) : (
              <div className="table-responsive">
                <Table
                  dataSource={bookings}
                  columns={columns}
                  rowKey="lead_id"
                  loading={false}
                  pagination={false}
                  onRow={(row) => ({
                    style: { cursor: "pointer" },
                    onClick: () => navigate(`/leads/demo-booking/${row.lead_id}`),
                    onMouseEnter: (e) => { e.currentTarget.style.background = "#f5f3ff"; },
                    onMouseLeave: (e) => { e.currentTarget.style.background = ""; },
                  })}
                  style={{ borderRadius: 0 }}
                  size="middle"
                />
              </div>
            )}

            {!isFetching && total > 0 && (
              <div style={{ padding: "16px 24px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "center", background: "#fafbff" }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                />
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default DemoBookingList;
